// Cache for loaded configuration
let configCache = null;
let configLoadPromise = null;

/**
 * Load configuration from config.json file
 * Returns null if config file doesn't exist or is invalid
 */
async function loadConfig() {
  if (configCache !== null) {
    return configCache;
  }

  if (configLoadPromise) {
    return configLoadPromise;
  }

  configLoadPromise = (async () => {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL || ''}/config.json`);
      if (!response.ok) {
        console.warn('Config file not found, using default configuration');
        return null;
      }
      
      const config = await response.json();
      
      // Check if config has valid URLs
      if (config && (config.backendUrl || config.apiBaseUrl)) {
        configCache = config;
        return config;
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to load config file, using default configuration:', error);
      return null;
    }
  })();

  return configLoadPromise;
}

/**
 * Get the backend API URL based on the current browser URL
 * In production (deployed): uses api subdomain (api.fastchat.maikvdhengel.me)
 * In development: uses different port (5000) with /api prefix
 * 
 * Priority order:
 * 1. config.json file (if backendUrl is set)
 * 2. Environment variable REACT_APP_API_URL
 * 3. Auto-detection based on current URL
 */
export async function getBackendUrl() {
  // Try to load config file first
  const config = await loadConfig();
  if (config && config.backendUrl) {
    return config.backendUrl;
  }
  // When apiBaseUrl is relative (e.g. "/api"), backend is same origin (combined deployment)
  if (config && config.apiBaseUrl && typeof config.apiBaseUrl === 'string' && config.apiBaseUrl.startsWith('/')) {
    return window.location.origin;
  }

  // Check if we have an environment variable override
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Get the current origin from the browser
  const origin = window.location.origin;
  
  try {
    const url = new URL(origin);
    
    // Check if we're in production/deployed mode
    // Production is indicated by:
    // - Standard HTTP/HTTPS ports (80, 443) or no port specified
    // - Not localhost (unless it's a production-like setup)
    const isProduction = 
      !url.port || 
      url.port === '80' || 
      url.port === '443' ||
      (process.env.NODE_ENV === 'production' && url.hostname !== 'localhost');
    
    if (isProduction) {
      // In production, use api subdomain (e.g., fastchat.maikvdhengel.me -> api.fastchat.maikvdhengel.me)
      url.hostname = `api.${url.hostname}`;
      // Remove port for standard HTTP/HTTPS
      if (!url.port || url.port === '80' || url.port === '443') {
        url.port = '';
      }
      return url.origin;
    } else {
      // In development, use port 5000 for backend
      url.port = '5000';
      return url.origin;
    }
  } catch (error) {
    // Fallback to localhost:5000 if URL parsing fails
    console.warn('Failed to parse URL, using default:', error);
    return 'http://localhost:5000';
  }
}

/**
 * Get the backend API base URL (with /api path)
 * 
 * Priority order:
 * 1. config.json file (if apiBaseUrl is set)
 * 2. getBackendUrl() + '/api'
 */
export async function getApiBaseUrl() {
  // Try to load config file first
  const config = await loadConfig();
  if (config && config.apiBaseUrl) {
    return config.apiBaseUrl;
  }

  // Fallback to constructing from backend URL
  const backendUrl = await getBackendUrl();
  return `${backendUrl}/api`;
}

